import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../../auth/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { buildApiUrl } from '../../config/runtime';

// --- Icons (Inline SVGs for reliability) ---
const Icons = {
    Dashboard: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
    ),
    List: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6"></line>
            <line x1="8" y1="12" x2="21" y2="12"></line>
            <line x1="8" y1="18" x2="21" y2="18"></line>
            <line x1="3" y1="6" x2="3.01" y2="6"></line>
            <line x1="3" y1="12" x2="3.01" y2="12"></line>
            <line x1="3" y1="18" x2="3.01" y2="18"></line>
        </svg>
    ),
    Settings: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
    ),
    Search: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
    ),
    User: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
        </svg>
    ),
    ChevronRight: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    )
};

const AdminDashboard = () => {
    const [results, setResults] = useState([]);
    const [filteredResults, setFilteredResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');

    // Sidebar active state
    const [activeMenu, setActiveMenu] = useState('results');

    const navigate = useNavigate();

    useEffect(() => {
        fetchResults();
    }, []);

    useEffect(() => {
        // Filter Logic
        let res = results;

        // 1. Filter by Test Type
        if (filterType !== 'all') {
            res = res.filter(item => item.test_type === filterType);
        }

        // 2. Filter by Search Term (User ID)
        if (searchTerm) {
            res = res.filter(item =>
                item.user_id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredResults(res);
    }, [results, filterType, searchTerm]);

    const fetchResults = async () => {
        try {
            const token = getToken();
            if (!token) {
                alert('로그인이 필요합니다.');
                navigate('/login');
                return;
            }

            const response = await axios.get(buildApiUrl('/admin/results'), {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Filter to keep only the latest result per user_id + test_type
            const allResults = response.data.results || [];
            if (!Array.isArray(allResults)) {
                console.error("Invalid data format received:", response.data);
                setResults([]);
                setFilteredResults([]);
                setLoading(false);
                return;
            }

            const latestMap = new Map();

            allResults.forEach(item => {
                const key = `${item.user_id}-${item.test_type}`;
                // Since the API returns sorted by created_at DESC, the first one we encounter is the latest.
                // However, to be safe against unsorted data, we compare dates.
                if (!latestMap.has(key)) {
                    latestMap.set(key, item);
                } else {
                    const existing = latestMap.get(key);
                    if (new Date(item.created_at) > new Date(existing.created_at)) {
                        latestMap.set(key, item);
                    }
                }
            });

            const uniqueResults = Array.from(latestMap.values())
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Re-sort by date

            setResults(uniqueResults);
            setFilteredResults(uniqueResults);
        } catch (error) {
            console.error('Failed to fetch results:', error);
            if (error.response && error.response.status === 403) {
                alert('관리자 권한이 없습니다.');
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper to get badge color for test type
    const getBadgeColor = (type) => {
        switch (type) {
            case 'mega': return { bg: 'rgba(59, 130, 246, 0.15)', text: '#60a5fa', border: '#3b82f6' };
            case 'face': return { bg: 'rgba(16, 185, 129, 0.15)', text: '#34d399', border: '#059669' };
            case 'chugumi': return { bg: 'rgba(236, 72, 153, 0.15)', text: '#f472b6', border: '#db2777' };
            case 'love': return { bg: 'rgba(244, 63, 94, 0.15)', text: '#fb7185', border: '#e11d48' };
            default: return { bg: 'rgba(148, 163, 184, 0.15)', text: '#cbd5e1', border: '#64748b' };
        }
    };

    // Layout Content
    return (
        <div style={styles.layout}>
            {/* Sidebar */}
            <aside style={styles.sidebar}>
                <div style={styles.sidebarHeader}>
                    <div style={styles.logo}>StartUs Admin</div>
                </div>
                <nav style={styles.nav}>
                    <MenuItem
                        icon={<Icons.Dashboard />}
                        label="대시보드"
                        active={activeMenu === 'dashboard'}
                        onClick={() => setActiveMenu('dashboard')}
                    />
                    <MenuItem
                        icon={<Icons.List />}
                        label="테스트 결과"
                        active={activeMenu === 'results'}
                        onClick={() => setActiveMenu('results')}
                    />
                    <MenuItem
                        icon={<Icons.Settings />}
                        label="설정"
                        active={activeMenu === 'settings'}
                        onClick={() => setActiveMenu('settings')}
                    />
                </nav>
                <div style={styles.sidebarFooter}>
                    <img src="https://ui-avatars.com/api/?name=Admin&background=random" alt="Admin" style={styles.avatar} />
                    <div style={styles.adminInfo}>
                        <div style={styles.adminName}>관리자</div>
                        <div style={styles.adminRole}>Super Admin</div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main style={styles.main}>
                {/* Header */}
                <header style={styles.header}>
                    <h1 style={styles.pageTitle}>테스트 결과 관리</h1>
                    <div style={styles.headerActions}>
                        <div style={styles.statusBadge}>System Online</div>
                    </div>
                </header>

                {/* Dashboard Area */}
                <div style={styles.contentArea}>

                    {/* Filters */}
                    <div style={styles.filterBar}>
                        <div style={styles.searchBox}>
                            <Icons.Search />
                            <input
                                type="text"
                                placeholder="User ID 검색..."
                                style={styles.searchInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            style={styles.selectFilter}
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">모든 테스트 유형</option>
                            <option value="mega">메가 시너지 (Mega)</option>
                            <option value="face">얼굴형 분석 (Face)</option>
                            <option value="chugumi">추구미 (Chugumi)</option>
                            <option value="love">연애 가치관 (Love)</option>
                        </select>
                    </div>

                    {/* Table */}
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>완료 일시</th>
                                    <th style={styles.th}>User ID</th>
                                    <th style={styles.th}>테스트 유형</th>
                                    <th style={styles.th}>결과 아키타입</th>
                                    <th style={styles.th}>액션</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    // Skeleton UI
                                    [...Array(5)].map((_, i) => (
                                        <tr key={i} style={styles.tr}>
                                            <td style={styles.td}><div style={styles.skeletonText}></div></td>
                                            <td style={styles.td}><div style={styles.skeletonText}></div></td>
                                            <td style={styles.td}><div style={styles.skeletonBadge}></div></td>
                                            <td style={styles.td}><div style={styles.skeletonText}></div></td>
                                            <td style={styles.td}><div style={styles.skeletonBtn}></div></td>
                                        </tr>
                                    ))
                                ) : filteredResults.length > 0 ? (
                                    filteredResults.map((item) => {
                                        const badgeStyle = getBadgeColor(item.test_type);
                                        return (
                                            <tr key={item.id} style={styles.tr}>
                                                <td style={styles.td}>{new Date(item.created_at).toLocaleString()}</td>
                                                <td style={{ ...styles.td, fontWeight: 'bold', color: '#fff' }}>{item.user_id}</td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        padding: '0.3rem 0.6rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        backgroundColor: badgeStyle.bg,
                                                        color: badgeStyle.text,
                                                        border: `1px solid ${badgeStyle.border}30`
                                                    }}>
                                                        {item.test_type}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    {item.result_label || '-'}
                                                </td>
                                                <td style={styles.td}>
                                                    <Link to={`/startus-admin/user/${item.user_id}`} style={styles.detailBtn}>
                                                        상세 보기 <Icons.ChevronRight />
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={styles.emptyTd}>검색 결과가 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
};

// Component for Menu Item
const MenuItem = ({ icon, label, active, onClick }) => (
    <div
        onClick={onClick}
        style={{
            ...styles.menuItem,
            ...(active ? styles.menuItemActive : {})
        }}
    >
        {icon}
        <span>{label}</span>
    </div>
);

// Styles
const styles = {
    layout: {
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: '#e2e8f0',
        fontFamily: "'Inter', sans-serif",
    },
    sidebar: {
        width: '260px',
        backgroundColor: '#1e293b',
        borderRight: '1px solid #334155',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        height: '100vh',
        zIndex: 10,
    },
    sidebarHeader: {
        padding: '2rem',
        borderBottom: '1px solid #334155',
    },
    logo: {
        fontSize: '1.5rem',
        fontWeight: '900',
        background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    nav: {
        flex: 1,
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        padding: '0.8rem 1rem',
        borderRadius: '8px',
        color: '#94a3b8',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontWeight: '500',
    },
    menuItemActive: {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#60a5fa',
    },
    sidebarFooter: {
        padding: '1.5rem',
        borderTop: '1px solid #334155',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    },
    avatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
    },
    adminInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    adminName: {
        fontSize: '0.9rem',
        fontWeight: '600',
        color: '#fff',
    },
    adminRole: {
        fontSize: '0.8rem',
        color: '#64748b',
    },
    main: {
        flex: 1,
        marginLeft: '260px', // Matches sidebar width
        padding: '2rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    pageTitle: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#fff',
    },
    statusBadge: {
        padding: '0.4rem 0.8rem',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        color: '#34d399',
        borderRadius: '100px',
        fontSize: '0.8rem',
        fontWeight: '600',
        border: '1px solid rgba(16, 185, 129, 0.2)',
    },
    contentArea: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
    },
    filterBar: {
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        backgroundColor: '#1e293b',
        padding: '1rem',
        borderRadius: '12px',
        border: '1px solid #334155',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.8rem',
        backgroundColor: '#0f172a',
        padding: '0.6rem 1rem',
        borderRadius: '8px',
        flex: 1,
        border: '1px solid #334155',
        color: '#94a3b8',
    },
    searchInput: {
        background: 'transparent',
        border: 'none',
        color: 'white',
        fontSize: '0.95rem',
        outline: 'none',
        width: '100%',
    },
    selectFilter: {
        backgroundColor: '#0f172a',
        color: 'white',
        border: '1px solid #334155',
        padding: '0.6rem 1rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        outline: 'none',
        cursor: 'pointer',
    },
    tableContainer: {
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        border: '1px solid #334155',
        overflow: 'hidden',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '1.2rem',
        borderBottom: '1px solid #334155',
        color: '#94a3b8',
        fontSize: '0.85rem',
        fontWeight: '600',
    },
    tr: {
        borderBottom: '1px solid #334155',
        transition: 'background 0.2s',
    },
    td: {
        padding: '1.2rem',
        color: '#cbd5e1',
        fontSize: '0.9rem',
    },
    emptyTd: {
        padding: '3rem',
        textAlign: 'center',
        color: '#64748b',
    },
    detailBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.4rem 0.8rem',
        fontSize: '0.8rem',
        borderRadius: '6px',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#60a5fa',
        textDecoration: 'none',
        transition: 'all 0.2s',
    },
    // Skeleton
    skeletonText: {
        height: '14px',
        width: '80%',
        backgroundColor: '#334155',
        borderRadius: '4px',
        opacity: 0.5,
    },
    skeletonBadge: {
        height: '24px',
        width: '60px',
        backgroundColor: '#334155',
        borderRadius: '6px',
        opacity: 0.5,
    },
    skeletonBtn: {
        height: '28px',
        width: '80px',
        backgroundColor: '#334155',
        borderRadius: '6px',
        opacity: 0.5,
    }
};

export default AdminDashboard;
